import { FC, MouseEvent, useCallback, useEffect, useMemo, useRef } from 'react';

import { push } from 'connected-react-router';
import {
    CampaignCreationWizardStep,
    createCampaignCreationWizardBreadcrumbClickHandler,
} from 'utils/campaignCreationWizardFlow/campaignCreationWizardFlow';
import { CREATE_MENU_PAGE_ROUTE } from 'utils/constants/app';
import { CampaignProductType } from 'utils/constants/campaign';

import { GridColumn, GridLayout, GridRow, useDebounce } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';
import { useCampaignCreationWizardBackAlert } from 'hooks/useCampaignCreationWizardBackAlert';
import { useCampaignCreationWizardStepNavigation } from 'hooks/useCampaignCreationWizardStepNavigation';
import { useDashboardRoute } from 'hooks/useDashboardRoute';

import AdQuickCreateBody from 'components/AdQuickCreate';

import { AdQuickCreateActionBar } from 'components/AdQuickCreate/AdQuickCreateActionBar';
import { PageContentHeader } from 'components/common/PageContentHeader/PageContentHeader';
import { VacancyLink } from 'components/common/VacancyLink/VacancyLink';
import { Header } from 'components/Header/Header';

import { WizardCpcMode } from 'store/models/campaignCreationWizard';
import {
    QuickCampaignFieldName,
    selectQuickCampaignFormDataField,
    selectQuickCampaignVacancyName,
} from 'store/models/createQuickCampaign';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    breadcrumbs: {
        main: 'AdQuickCreate.breadcrumbs.main',
        cpc: 'AdQuickCreate.breadcrumbs.cpc',
        settings: 'CampaignCreationWizardFlow.breadcrumbs.settings',
        creation: 'CampaignCreationWizard.breadcrumbs.creation',
        promotionType: 'CampaignCreationWizard.breadcrumbs.promotionType',
    },
};

export const AdQuickCreate: FC = () => {
    const vacancyName = useSelector(selectQuickCampaignVacancyName);
    const vacancyId = useSelector(selectQuickCampaignFormDataField[QuickCampaignFieldName.VacancyId]);

    const dispatch = useDispatch();
    const trl = useTrls();
    const dashboardRoute = useDashboardRoute();
    const isWizardFlowEnabled = Boolean(vacancyId);
    const { backAlert, openBackAlert } = useCampaignCreationWizardBackAlert();
    const goToWizardStep = useCampaignCreationWizardStepNavigation();

    const quickWizardState = useMemo(
        () => ({
            vacancyId: Number(vacancyId) || null,
            vacancyName: vacancyName ?? undefined,
            productType: CampaignProductType.Cpc,
            cpcMode: WizardCpcMode.Quick,
        }),
        [vacancyId, vacancyName]
    );

    const vacancyAdditionalContent = vacancyId && vacancyName && (
        <VacancyLink dataQa="ad-quick-create-vacancy-link" vacancyId={vacancyId} vacancyName={vacancyName} />
    );

    const openWizardBackAlert = useCallback(
        (activeStep: CampaignCreationWizardStep) => {
            openBackAlert(() => goToWizardStep(activeStep, quickWizardState));
        },
        [openBackAlert, goToWizardStep, quickWizardState]
    );

    const breadcrumbsConfig = useMemo(() => {
        if (isWizardFlowEnabled) {
            return [
                {
                    to: dashboardRoute,
                    children: trl(TrlKeys.breadcrumbs.main),
                    onClick: (e: MouseEvent) => {
                        e.preventDefault();
                        openBackAlert(() => dispatch(push(dashboardRoute)));
                    },
                },
                {
                    to: CREATE_MENU_PAGE_ROUTE,
                    children: trl(TrlKeys.breadcrumbs.creation),
                    onClick: createCampaignCreationWizardBreadcrumbClickHandler(
                        openWizardBackAlert,
                        CampaignCreationWizardStep.PromotionObject
                    ),
                },
                {
                    to: CREATE_MENU_PAGE_ROUTE,
                    children: trl(TrlKeys.breadcrumbs.promotionType),
                    onClick: createCampaignCreationWizardBreadcrumbClickHandler(
                        openWizardBackAlert,
                        CampaignCreationWizardStep.PromotionType
                    ),
                },
                { to: '#', children: trl(TrlKeys.breadcrumbs.settings) },
            ];
        }

        return [
            {
                to: dashboardRoute,
                children: trl(TrlKeys.breadcrumbs.main),
            },
            { to: '#', children: trl(TrlKeys.breadcrumbs.cpc) },
        ];
    }, [dashboardRoute, isWizardFlowEnabled, dispatch, openBackAlert, openWizardBackAlert, trl]);

    const containerRef = useRef<HTMLDivElement>(null);
    const actionBarRef = useRef<HTMLDivElement>(null);

    const updateContainerHeight = useCallback(() => {
        if (containerRef.current && actionBarRef.current) {
            containerRef.current.style.minHeight = `calc(100vh - ${actionBarRef.current.offsetHeight}px)`;
        }
    }, []);

    const debouncedHandler = useDebounce(updateContainerHeight, 100);

    useEffect(() => {
        updateContainerHeight();
        window.addEventListener('resize', debouncedHandler);

        return () => {
            window.removeEventListener('resize', debouncedHandler);
        };
    }, [updateContainerHeight, debouncedHandler]);

    return (
        <>
            <div ref={containerRef}>
                <Header />
                <PageContentHeader
                    breadcrumbsConfig={breadcrumbsConfig}
                    title={trl(isWizardFlowEnabled ? TrlKeys.breadcrumbs.settings : TrlKeys.breadcrumbs.cpc)}
                    additionalContent={vacancyAdditionalContent}
                    vSpacingBottom={24}
                />
                <GridLayout>
                    <GridRow>
                        <GridColumn xs={4} s={8} m={12}>
                            <AdQuickCreateBody />
                        </GridColumn>
                    </GridRow>
                </GridLayout>
            </div>

            <AdQuickCreateActionBar actionBarRef={actionBarRef} />
            {isWizardFlowEnabled && backAlert}
        </>
    );
};
